<template>
  <div>
    <h5 class="etapa-titulo">Como o cliente escolhe o SKU?</h5>
    <p class="step-description">
      Cada variação vira uma opção para o cliente. A ordem dos cartões é a ordem
      que ele vê.
    </p>

    <div v-if="!campos.length" class="estado-vazio text-center">
      <div class="scope-icon scope-icon--accent mx-auto mb-2">
        <CIcon name="cil-grid" />
      </div>
      <strong class="d-block">Nenhuma variação ainda</strong>
      <small class="d-block text-muted mb-3">
        O que muda de um SKU para o outro: cor, tamanho, voltagem, sabor.
      </small>
      <CButton color="primary" @click="adicionar">
        <CIcon name="cil-plus" size="sm" class="mr-1" />
        Adicionar a primeira variação
      </CButton>
    </div>

    <template v-else>
      <draggable :list="campos" handle=".variacao__arrasta" :animation="180" @end="sincronizarOrdem">
        <CCard v-for="(campo, indice) in campos" :key="`campo-${indice}`" class="mb-2">
          <CCardHeader class="d-flex align-items-center">
            <span class="variacao__arrasta mr-2" title="Arraste para reordenar">
              <fa-icon icon="grip-vertical" />
            </span>
            <CBadge color="primary" class="mr-2">{{ indice + 1 }}ª opção</CBadge>
            <small class="text-muted text-truncate variacao__preview">
              o cliente vê <strong>{{ nomeExibido(campo) }}</strong>
            </small>
            <CButton
              class="ml-auto"
              size="sm"
              color="danger"
              variant="outline"
              title="Remover variação"
              @click="remover(indice)"
            >
              <fa-icon icon="trash-alt" />
            </CButton>
          </CCardHeader>

          <CCardBody>
            <CRow>
              <CCol col="12" md="4">
                <CSelect
                  label="Atributo do produto *"
                  placeholder="Escolha o atributo..."
                  :value="campo.colunaProduto"
                  :options="opcoesColuna(campo.colunaProduto)"
                  @update:value="alterarColuna(campo, $event)"
                />
              </CCol>

              <CCol col="12" md="4">
                <CInput
                  label="Apelido"
                  v-model="campo.labelCustomizado"
                  :placeholder="labelPadrao(campo)"
                  maxlength="60"
                />
              </CCol>

              <CCol col="12" md="4">
                <CSelect
                  label="Ordem dos valores"
                  :value="campo.ordenacao || 'natural'"
                  :options="opcoesOrdenacao"
                  @update:value="alterarOrdenacao(campo, $event)"
                />
              </CCol>
            </CRow>

            <template v-if="campo.ordenacao === 'manual'">
              <CButton
                size="sm"
                color="info"
                variant="outline"
                :disabled="!campo.colunaProduto"
                @click="alternarOrdemManual(indice)"
              >
                <fa-icon icon="sort" class="mr-1" />
                {{
                  indice === indiceEmEdicao ? "Fechar ordenação manual" : rotuloOrdemManual(campo)
                }}
              </CButton>
              <small v-if="!campo.colunaProduto" class="text-muted ml-2">
                Escolha o atributo para listar os valores.
              </small>

              <div v-if="indice === indiceEmEdicao" class="ordem-manual mt-3">
                <CRow>
                  <CCol col="12" md="6" class="mb-3 mb-md-0">
                    <label class="ordem-manual__legenda">
                      Ordem definida ({{ ordemAtual.length }})
                    </label>

                    <p v-if="!ordemAtual.length" class="text-muted small mb-0">
                      Nenhum valor posicionado — a ordem automática vale para todos.
                    </p>

                    <draggable
                      v-else
                      v-model="ordemAtual"
                      handle=".ordem-manual__arrasta"
                      :animation="200"
                    >
                      <CListGroupItem
                        v-for="(valor, posicao) in ordemAtual"
                        :key="`ordem-${valor}`"
                        class="d-flex align-items-center py-2"
                      >
                        <span class="ordem-manual__arrasta mr-2">
                          <fa-icon icon="grip-vertical" />
                        </span>
                        <CBadge color="info" class="mr-2">{{ posicao + 1 }}</CBadge>
                        <span class="flex-fill">{{ rotuloValor(valor) }}</span>
                        <CButton size="sm" color="link" @click="removerValor(valor)">
                          <fa-icon icon="times" />
                        </CButton>
                      </CListGroupItem>
                    </draggable>
                  </CCol>

                  <CCol col="12" md="6">
                    <label class="ordem-manual__legenda">
                      Valores disponíveis
                      <small class="text-muted font-weight-normal text-lowercase">
                        (só os que os produtos deste grupo têm)
                      </small>
                    </label>

                    <CInput v-model="buscaValor" placeholder="Buscar valor..." size="sm" />

                    <p v-if="carregandoValores" class="text-muted small mb-0">
                      <CSpinner size="sm" class="mr-1" />
                      Carregando...
                    </p>
                    <p v-else-if="!valoresDisponiveis.length" class="text-muted small mb-0">
                      Nenhum valor encontrado para este atributo.
                    </p>
                    <p v-else-if="!valoresFiltrados.length" class="text-muted small mb-0">
                      Todos os valores já foram posicionados.
                    </p>
                    <div v-else class="ordem-manual__disponiveis">
                      <CButton
                        v-for="valor in valoresFiltrados"
                        :key="`disponivel-${valor.id}`"
                        size="sm"
                        color="secondary"
                        variant="outline"
                        class="mr-1 mb-1"
                        @click="adicionarValor(valor.id)"
                      >
                        <fa-icon icon="plus" class="mr-1" />
                        {{ valor.label }}
                      </CButton>
                    </div>
                  </CCol>
                </CRow>
              </div>
            </template>
          </CCardBody>
        </CCard>
      </draggable>

      <CButton
        color="primary"
        variant="outline"
        size="sm"
        :disabled="campos.length >= LIMITE_CAMPOS"
        @click="adicionar"
      >
        <CIcon name="cil-plus" size="sm" class="mr-1" />
        Adicionar variação
      </CButton>
      <small class="text-muted ml-2">
        {{ campos.length }} de {{ LIMITE_CAMPOS }} variações
        <template v-if="campos.length >= LIMITE_CAMPOS">— limite atingido</template>
      </small>
    </template>
  </div>
</template>

<script>
import { get } from "@/helpers/apiRequest";
import draggable from "vuedraggable";
import { LIMITE_CAMPOS } from "./etapas";

// Espelha App\Constants\OrdenacaoGrade na API. O que cada modo faz está no
// "Como funciona" da etapa, não repetido embaixo de cada select.
const MODOS = [
  { valor: "natural", label: "Automática (recomendada)" },
  { valor: "alfabetica", label: "Alfabética" },
  { valor: "numerica", label: "Numérica" },
  { valor: "cadastro", label: "Ordem de cadastro" },
  { valor: "manual", label: "Manual" },
];

export default {
  name: "EtapaVariacoes",
  components: { draggable },
  props: {
    form: { type: Object, required: true },
    colunasDisponiveis: { type: Array, default: () => [] },
  },
  data() {
    return {
      LIMITE_CAMPOS,
      indiceEmEdicao: null,
      valoresDisponiveis: [],
      carregandoValores: false,
      buscaValor: "",
    };
  },
  computed: {
    campos() {
      if (!this.form.campos) {
        this.$set(this.form, "campos", []);
      }
      return this.form.campos;
    },
    opcoesOrdenacao() {
      return MODOS.map((modo) => ({ value: modo.valor, label: modo.label }));
    },
    campoEmEdicao() {
      if (this.indiceEmEdicao === null) return null;
      return this.campos[this.indiceEmEdicao] || null;
    },
    // Lista da ordem manual do campo aberto. O setter mantém o array do
    // formulário sincronizado com o arrasta-e-solta.
    ordemAtual: {
      get() {
        return (this.campoEmEdicao && this.campoEmEdicao.ordemValores) || [];
      },
      set(novaOrdem) {
        this.$set(this.campoEmEdicao, "ordemValores", novaOrdem);
      },
    },
    valoresFiltrados() {
      const termo = this.buscaValor.trim().toLowerCase();
      const jaPosicionados = new Set(this.ordemAtual.map((valor) => String(valor)));

      return this.valoresDisponiveis
        .filter((valor) => !jaPosicionados.has(String(valor.id)))
        .filter((valor) => !termo || String(valor.label).toLowerCase().includes(termo));
    },
  },
  methods: {
    adicionar() {
      if (this.campos.length >= LIMITE_CAMPOS) return;
      this.campos.push({
        colunaProduto: null,
        labelCustomizado: "",
        ordem: this.campos.length,
        eixo: null,
        ordenacao: "natural",
        ordemValores: [],
      });
    },
    remover(indice) {
      this.campos.splice(indice, 1);
      this.indiceEmEdicao = null;
      this.sincronizarOrdem();
    },
    // A ordem enviada para a API é a posição do cartão na tela: quem arrasta
    // não deveria precisar entender um campo numérico de ordem.
    sincronizarOrdem() {
      this.campos.forEach((campo, indice) => {
        this.$set(campo, "ordem", indice);
      });
      this.indiceEmEdicao = null;
    },
    alterarOrdenacao(campo, modo) {
      this.$set(campo, "ordenacao", modo);

      if (modo !== "manual") {
        this.indiceEmEdicao = null;
        return;
      }

      if (!campo.ordemValores) {
        this.$set(campo, "ordemValores", []);
      }
    },
    alternarOrdemManual(indice) {
      if (this.indiceEmEdicao === indice) {
        this.indiceEmEdicao = null;
        return;
      }

      this.indiceEmEdicao = indice;
      this.buscaValor = "";
      this.carregarValores(this.campos[indice].colunaProduto);
    },
    carregarValores(coluna) {
      if (!coluna) {
        this.valoresDisponiveis = [];
        return;
      }

      // As condições do grupo restringem a lista aos valores que os produtos
      // deste grupo realmente têm — sem elas viria o catálogo inteiro.
      const regras = (this.form.regras || []).filter(
        (regra) => regra.colunaProduto && regra.valor
      );
      const filtro = regras.length
        ? `&regras=${encodeURIComponent(JSON.stringify(regras))}`
        : "";

      this.carregandoValores = true;
      get(`/admin/grupo-grade/valores-coluna?coluna=${encodeURIComponent(coluna)}${filtro}`)
        .then((json) => {
          this.valoresDisponiveis = json.data || json || [];
        })
        .catch(() => {
          this.valoresDisponiveis = [];
        })
        .finally(() => {
          this.carregandoValores = false;
        });
    },
    adicionarValor(valor) {
      this.ordemAtual = [...this.ordemAtual, String(valor)];
    },
    removerValor(valor) {
      this.ordemAtual = this.ordemAtual.filter((atual) => String(atual) !== String(valor));
    },
    // Valores relacionais (dimensão, marca) são gravados pelo id; mostramos a
    // descrição quando ela existe na lista carregada.
    rotuloValor(valor) {
      const encontrado = this.valoresDisponiveis.find(
        (disponivel) => String(disponivel.id) === String(valor)
      );
      return encontrado ? encontrado.label : valor;
    },
    rotuloOrdemManual(campo) {
      const total = (campo.ordemValores || []).length;
      return total ? `Ordenar valores (${total} posicionados)` : "Ordenar valores";
    },
    labelPadrao(campo) {
      if (!campo.colunaProduto) return "ex: Cor, Tamanho";
      const opcao = (this.colunasDisponiveis || []).find(
        (item) => item.coluna === campo.colunaProduto
      );
      return opcao ? opcao.label : "ex: Cor, Tamanho";
    },
    nomeExibido(campo) {
      const apelido = (campo.labelCustomizado || "").trim();
      if (apelido) return apelido;
      if (!campo.colunaProduto) return "—";
      return this.labelPadrao(campo);
    },
    // Trocar o atributo invalida a ordem manual: os valores eram de outra coluna.
    alterarColuna(campo, coluna) {
      this.$set(campo, "colunaProduto", coluna);
      this.$set(campo, "ordemValores", []);
      this.indiceEmEdicao = null;
    },
    opcoesColuna(colunaAtual) {
      const colunasUsadas = this.campos
        .map((campo) => campo.colunaProduto)
        .filter((coluna) => coluna && coluna !== colunaAtual);

      return this.colunasDisponiveis
        .filter((opcao) => !colunasUsadas.includes(opcao.coluna))
        .map((opcao) => ({ value: opcao.coluna, label: opcao.label }));
    },
  },
};
</script>

<style scoped>
.etapa-titulo {
  font-weight: 600;
  color: #1d2939;
}

.step-description {
  color: #667085;
  font-size: 13px;
  margin-bottom: 20px;
  max-width: 72ch;
}

.estado-vazio {
  padding: 32px 24px;
  border: 1px dashed #dddce9;
  border-radius: 10px;
  background: #f9fafb;
}

.scope-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f2f2f8;
  color: #3a3e81;
}

.scope-icon--accent {
  background: #edf8f9;
  color: #2298a3;
}

/* text-truncate só encolhe dentro de um flex se o item puder ir a zero. */
.variacao__preview {
  min-width: 0;
}

.variacao__arrasta,
.ordem-manual__arrasta {
  color: #98a2b3;
  cursor: grab;
}

.ordem-manual {
  padding: 14px;
  border-radius: 8px;
  background: #f9fafb;
}

.ordem-manual__legenda {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #667085;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.ordem-manual__disponiveis {
  max-height: 220px;
  overflow-y: auto;
}
</style>
